import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ParticlesComponent } from '../../shared/components/particles/particles.component';
import { HeroSliderComponent } from '../../shared/components/hero-slider/hero-slider.component';
import { CounterComponent } from '../../shared/components/counter/counter.component';
import { BeforeAfterComponent } from '../../shared/components/before-after/before-after.component';
import { InlinePromotionComponent } from '../../shared/components/inline-promotion/inline-promotion.component';
import { InlinePromotionData } from '../../core/config/site-config.interface';
import { ScrollAnimateDirective } from '../../shared/directives/scroll-animate.directive';
import { SeoService } from '../../core/services/seo.service';
import { SiteConfigService } from '../../core/config/site-config.service';
import { ImgFallbackDirective } from '../../shared/directives/img-fallback.directive';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [RouterLink, ParticlesComponent, HeroSliderComponent, CounterComponent, BeforeAfterComponent, InlinePromotionComponent, ScrollAnimateDirective, ImgFallbackDirective],
  template: `
    <!-- ══════ HERO ══════ -->
    <section class="relative w-full min-h-screen flex items-center bg-surface-950 overflow-hidden -mt-20 pt-20">
      @if (heroSlides.length) {
        <app-hero-slider [slides]="heroSlides" />
      } @else if (hero.backgroundImage) {
        <img [src]="hero.backgroundImage" alt="" class="absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
        <div class="absolute inset-0 bg-surface-950/60 z-[1]"></div>
      }
      <app-particles class="absolute inset-0 z-[3] pointer-events-none" [count]="70" [connectionDistance]="140" [mouseRadius]="200" />
      <div class="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-surface-950 to-transparent z-[4]"></div>

      @if (!heroSlides.length) {
        <div class="relative z-[5] w-full px-6 lg:px-8 py-32 md:py-40">
          <div class="mx-auto max-w-7xl">
            <div class="max-w-3xl">
              <div class="section-label section-label-light mb-6">{{ hero.label }}</div>
              <h1 class="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-[1.1] tracking-[-0.02em] mb-7">
                {{ hero.title }}
                <span class="text-brand-300"> {{ hero.highlight }}</span>
              </h1>
              <p class="text-lg md:text-xl text-surface-300 leading-relaxed max-w-xl mb-10">
                {{ hero.description }}
              </p>
              <div class="flex flex-wrap items-center gap-4">
                <a [routerLink]="hero.ctaPrimary.link"
                   class="inline-flex items-center gap-2.5 px-8 py-3.5 bg-brand-500 text-white font-semibold text-sm rounded-lg transition-all duration-300 hover:bg-brand-600 hover:shadow-lg hover:shadow-brand-500/25">
                  {{ hero.ctaPrimary.text }}
                  <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </a>
                @if (hero.ctaSecondary) {
                  <a [href]="hero.ctaSecondary.link" [attr.aria-label]="hero.ctaSecondary.text"
                     class="inline-flex items-center gap-2.5 px-8 py-3.5 border border-white/20 text-white font-semibold text-sm rounded-lg transition-all duration-300 hover:bg-white/[0.06] hover:border-white/30">
                    <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                    {{ hero.ctaSecondary.text }}
                  </a>
                }
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Scroll indicator -->
      <div class="absolute bottom-10 left-1/2 -translate-x-1/2 z-[5] flex flex-col items-center gap-2 animate-bounce" aria-hidden="true">
        <span class="text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-surface-500">Scroll</span>
        <svg aria-hidden="true" class="w-4 h-4 text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" /></svg>
      </div>
    </section>

    <!-- ══════ TRUSTED BY ══════ -->
    <section class="py-16 border-b border-surface-100 bg-white">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <p class="text-center text-xs font-semibold uppercase tracking-[0.2em] text-surface-400 mb-10">Trusted by homeowners & businesses across South Florida</p>
        <div class="flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
          @for (partner of partners; track partner.name) {
            <div class="flex flex-col items-center gap-2.5 opacity-60 hover:opacity-100 transition-opacity duration-300">
              <img [src]="partner.logo" [alt]="partner.name" [appImgFallback]="partner.name" class="w-20 h-20 rounded-xl object-cover" />
              <span class="text-xs font-medium text-surface-400">{{ partner.name }}</span>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- ══════ SERVICES PREVIEW ══════ -->
    <section class="py-24 md:py-32 bg-white">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="flex flex-col md:flex-row md:items-end md:justify-between mb-16" appScrollAnimate data-animate>
          <div class="max-w-xl mb-6 md:mb-0">
            <div class="section-label">What We Do</div>
            <h2 class="font-heading text-3xl md:text-4xl font-bold text-surface-900 tracking-[-0.025em]">
              Complete Home & Commercial Solutions
            </h2>
          </div>
          <a routerLink="/services" class="group inline-flex items-center gap-2 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors">
            All Services
            <svg aria-hidden="true" class="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
          </a>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          @for (service of services; track service.title; let i = $index) {
            <a routerLink="/services" appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="(i + 1) * 100"
               class="group p-7 rounded-2xl border border-surface-100 bg-white transition-all duration-300 hover:shadow-xl hover:shadow-surface-900/[0.05] hover:border-surface-200 hover:-translate-y-1">
              <div class="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-colors duration-300"
                   [class]="'bg-brand-50 text-brand-600 group-hover:bg-brand-600 group-hover:text-white'">
                <span class="text-xl" [innerHTML]="service.icon"></span>
              </div>
              <h3 class="font-heading text-lg font-bold text-surface-900 mb-2 group-hover:text-brand-700 transition-colors">{{ service.title }}</h3>
              <p class="text-sm text-surface-500 leading-relaxed">{{ service.desc }}</p>
            </a>
          }
        </div>
      </div>
    </section>

    <!-- ══════ INLINE PROMOTION ══════ -->
    @if (homePromo) {
      <app-inline-promotion [promo]="homePromo" />
    }

    <!-- ══════ ABOUT SNIPPET ══════ -->
    <section class="py-24 md:py-32 bg-surface-50">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <!-- Image -->
          <div appScrollAnimate data-animate="slide-left"
               class="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-surface-200 via-surface-100 to-surface-200">
            @if (aboutSnippet.image) {
              <img [src]="aboutSnippet.image" [alt]="brandName" [appImgFallback]="brandName" class="absolute inset-0 w-full h-full object-cover" />
            } @else {
              <div class="absolute inset-0 flex items-center justify-center">
                <svg aria-hidden="true" class="w-16 h-16 text-brand-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 0h.008v.008h-.008V7.5z" /></svg>
              </div>
            }
            <!-- Decorative corner -->
            <div class="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-brand-500/20 rounded-2xl"></div>
            <!-- Experience badge -->
            <div class="absolute bottom-5 left-5 bg-brand-600 text-white px-5 py-3.5 rounded-xl shadow-lg">
              <div class="font-heading text-2xl font-extrabold">{{ stats[0].value }}{{ stats[0].suffix }}</div>
              <div class="text-[0.65rem] font-semibold uppercase tracking-wider text-brand-200">{{ stats[0].label }}</div>
            </div>
          </div>

          <!-- Text -->
          <div appScrollAnimate data-animate="slide-right">
            <div class="section-label">{{ aboutSnippet.label }}</div>
            <h2 class="font-heading text-3xl md:text-4xl font-bold text-surface-900 tracking-[-0.025em] mb-6">
              {{ aboutSnippet.heading }}
            </h2>
            @for (p of aboutSnippet.paragraphs; track $index) {
              <p class="text-surface-500 leading-relaxed" [class.mb-6]="!$last" [class.mb-8]="$last">{{ p }}</p>
            }
            <div class="flex flex-wrap items-center gap-6 mb-8">
              @for (badge of badges; track badge) {
                <div class="flex items-center gap-2 text-sm">
                  <svg aria-hidden="true" class="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span class="font-medium text-surface-700">{{ badge }}</span>
                </div>
              }
            </div>
            <a routerLink="/about" class="group inline-flex items-center gap-2 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors">
              Read Our Full Story
              <svg aria-hidden="true" class="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- ══════ STATS ══════ -->
    <section class="py-20 bg-brand-900 relative overflow-hidden">
      <div class="absolute inset-0 bg-gradient-to-r from-brand-950 via-brand-900 to-brand-800"></div>
      <div class="absolute inset-0 opacity-[0.03]" style="background-image: url('data:image/svg+xml,%3Csvg width=&quot;40&quot; height=&quot;40&quot; viewBox=&quot;0 0 40 40&quot; xmlns=&quot;http://www.w3.org/2000/svg&quot;%3E%3Cpath d=&quot;M0 0h40v40H0z&quot; fill=&quot;none&quot;/%3E%3Cpath d=&quot;M0 20h40M20 0v40&quot; stroke=&quot;%23fff&quot; stroke-width=&quot;0.5&quot;/%3E%3C/svg%3E');"></div>
      <div class="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-10">
          @for (stat of stats; track stat.label) {
            <div class="text-center">
              <div class="font-heading text-3xl md:text-5xl font-extrabold text-white tracking-tight">
                <app-counter [target]="stat.value" [suffix]="stat.suffix" [duration]="2000" />
              </div>
              <div class="text-sm text-brand-300/80 mt-2 font-medium">{{ stat.label }}</div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- ══════ WHY CHOOSE US ══════ -->
    <section class="py-24 md:py-32 bg-white">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="text-center mb-16" appScrollAnimate data-animate>
          <div class="section-label justify-center">Why Choose Us</div>
          <h2 class="font-heading text-3xl md:text-4xl font-bold text-surface-900 tracking-[-0.025em] mb-4">
            {{ whyUsHeading }}
          </h2>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          @for (benefit of benefits; track benefit.title; let i = $index) {
            <div appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="(i + 1) * 100"
                 class="flex items-start gap-4 p-6 rounded-2xl bg-surface-50 border border-surface-100 transition-all duration-300 hover:shadow-lg hover:shadow-surface-900/[0.04]">
              <div class="w-11 h-11 rounded-xl bg-brand-100 text-brand-600 flex items-center justify-center shrink-0">
                <span class="text-lg" [innerHTML]="benefit.icon"></span>
              </div>
              <div>
                <h3 class="font-heading text-[0.9375rem] font-bold text-surface-900 mb-1">{{ benefit.title }}</h3>
                <p class="text-sm text-surface-500 leading-relaxed">{{ benefit.desc }}</p>
              </div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- ══════ BEFORE & AFTER ══════ -->
    <section class="py-24 md:py-32 bg-surface-50">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="text-center mb-16" appScrollAnimate data-animate>
          <div class="section-label justify-center">Transformations</div>
          <h2 class="font-heading text-3xl md:text-4xl font-bold text-surface-900 tracking-[-0.025em] mb-4">
            Before & After
          </h2>
          <p class="text-surface-500 max-w-2xl mx-auto">See the dramatic difference our work makes. Drag the slider to reveal the transformation.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
          @for (item of beforeAfterItems; track item.title; let i = $index) {
            <div appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="(i + 1) * 150">
              <app-before-after
                [beforeImage]="item.before"
                [afterImage]="item.after"
                aspectRatio="16/10"
              />
              <h4 class="font-heading text-base font-bold text-surface-900 mt-5">{{ item.title }}</h4>
              <p class="text-sm text-surface-500">{{ item.location }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- ══════ OUR VENDORS ══════ -->
    <section class="py-20 bg-white border-y border-surface-100">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="text-center mb-12" appScrollAnimate data-animate>
          <div class="section-label justify-center">Our Vendors</div>
          <h2 class="font-heading text-2xl md:text-3xl font-bold text-surface-900 tracking-[-0.025em]">
            Premium Materials from Trusted Brands
          </h2>
        </div>
        <div class="flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
          @for (vendor of vendors; track vendor.name) {
            <div class="flex flex-col items-center gap-2.5 opacity-50 hover:opacity-100 transition-opacity duration-300">
              <img [src]="vendor.logo" [alt]="vendor.name" [appImgFallback]="vendor.name" class="w-20 h-20 rounded-xl object-cover" />
              <span class="text-xs font-medium text-surface-400">{{ vendor.name }}</span>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- ══════ TESTIMONIALS / GOOGLE REVIEWS ══════ -->
    <section class="py-24 md:py-32 bg-surface-50">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="text-center mb-16" appScrollAnimate data-animate>
          <div class="section-label justify-center">Testimonials</div>
          <h2 class="font-heading text-3xl md:text-4xl font-bold text-surface-900 tracking-[-0.025em] mb-4">
            What Our Clients Say
          </h2>
          <div class="flex items-center justify-center gap-2 mb-2">
            <div class="stars" role="img" aria-label="4.9 out of 5 star rating">
              @for (s of [1,2,3,4,5]; track s) {
                <svg aria-hidden="true" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
              }
            </div>
            <span class="text-sm font-semibold text-surface-700">4.9 / 5.0</span>
            <span class="text-sm text-surface-400">— 200+ Google Reviews</span>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          @for (review of reviews; track review.name; let i = $index) {
            <div appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="(i + 1) * 100"
                 class="p-7 rounded-2xl bg-white border border-surface-100 shadow-sm">
              <div class="stars mb-4" role="img" aria-label="5 star rating">
                @for (s of [1,2,3,4,5]; track s) {
                  <svg aria-hidden="true" class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                }
              </div>
              <p class="text-sm text-surface-600 leading-relaxed mb-6 italic">"{{ review.text }}"</p>
              <div class="flex items-center gap-3 pt-5 border-t border-surface-100">
                <div class="w-10 h-10 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-heading font-bold text-xs">
                  {{ review.initials }}
                </div>
                <div>
                  <div class="text-sm font-semibold text-surface-900">{{ review.name }}</div>
                  <div class="text-xs text-surface-400 flex items-center gap-1.5">
                    <svg aria-hidden="true" class="w-3 h-3" viewBox="0 0 24 24" fill="#4285F4"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                    {{ review.date }}
                  </div>
                </div>
              </div>
            </div>
          }
        </div>

        <div class="text-center mt-12">
          <a href="#" class="inline-flex items-center gap-2 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors">
            See All Reviews on Google
            <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
          </a>
        </div>
      </div>
    </section>

    <!-- ══════ INLINE AD SLOT ══════ -->
    @if (adSlot) {
      <app-inline-promotion [promo]="adSlot" />
    }

    <!-- ══════ HOW IT WORKS ══════ -->
    <section class="py-24 md:py-32 bg-white">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="text-center mb-16" appScrollAnimate data-animate>
          <div class="section-label justify-center">How It Works</div>
          <h2 class="font-heading text-3xl md:text-4xl font-bold text-surface-900 tracking-[-0.025em]">
            Simple 4-Step Process
          </h2>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          @for (step of process; track step.title; let i = $index) {
            <div appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="(i + 1) * 100"
                 class="relative text-center p-8 rounded-2xl bg-surface-50 border border-surface-100">
              <div class="w-14 h-14 rounded-full bg-brand-600 text-white flex items-center justify-center mx-auto mb-6">
                <span class="font-heading text-xl font-bold">{{ i + 1 }}</span>
              </div>
              <h3 class="font-heading text-base font-bold text-surface-900 mb-2">{{ step.title }}</h3>
              <p class="text-sm text-surface-500 leading-relaxed">{{ step.desc }}</p>
              @if (i < process.length - 1) {
                <div class="hidden lg:block absolute top-14 -right-3 text-surface-300">
                  <svg aria-hidden="true" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                </div>
              }
            </div>
          }
        </div>
      </div>
    </section>

    <!-- ══════ CTA ══════ -->
    <section class="py-24 bg-brand-900 relative overflow-hidden">
      <div class="absolute inset-0 bg-gradient-to-br from-brand-950 via-brand-900 to-brand-800"></div>
      <div class="mx-auto max-w-4xl px-6 lg:px-8 text-center relative z-10">
        <h2 class="font-heading text-3xl md:text-4xl font-bold text-white mb-5 tracking-[-0.025em]">Ready to Start Your Project?</h2>
        <p class="text-brand-200/80 mb-10 max-w-lg mx-auto text-lg">Get a free, no-obligation estimate from South Florida's most trusted general contractor.</p>
        <div class="flex flex-wrap items-center justify-center gap-4">
          <a routerLink="/contact" class="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-brand-800 font-semibold text-sm rounded-lg hover:bg-brand-50 transition-all duration-300">
            Get Free Estimate
            <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
          </a>
          <a [href]="'tel:' + contactPhone.replace(/[^+\\d]/g, '')" class="inline-flex items-center gap-2 px-8 py-3.5 border border-white/25 text-white font-semibold text-sm rounded-lg hover:bg-white/10 transition-all duration-300">
            Call {{ contactPhone }}
          </a>
        </div>
      </div>
    </section>
  `,
})
export class HomeComponent implements OnInit {
  private seo = inject(SeoService);
  private config = inject(SiteConfigService);

  // Page data from config
  private homePage = this.config.pages.home;

  hero = this.homePage.hero;
  heroSlides = this.hero.slides || [];
  partners = this.homePage.partners;
  services = this.homePage.services;
  aboutSnippet = this.homePage.aboutSnippet;
  stats = this.homePage.stats;
  badges = this.homePage.badges;
  whyUsHeading = this.homePage.whyUsHeading;
  benefits = this.homePage.benefits;
  brandName = this.config.brand.name;
  contactPhone = this.config.contact.phone;
  beforeAfterItems = this.homePage.beforeAfterItems;
  vendors = this.homePage.vendors;
  reviews = this.homePage.reviews;
  process = this.homePage.process;

  // Promotions from config
  homePromo: InlinePromotionData | undefined = this.config.promotions.homeInline;
  adSlot: InlinePromotionData | undefined = this.config.promotions.homeAd;

  ngOnInit(): void {
    this.seo.setJsonLd([
      this.seo.getLocalBusinessSchema(),
      this.seo.getBreadcrumbSchema([{ name: 'Home', url: '/' }]),
    ]);
  }
}
